import React, { useState, useEffect } from 'react';
import { Users, Upload, TrendingUp, Award, BarChart3, FileSpreadsheet, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InstitutionManagementPage = () => {
  const [activeTab, setActiveTab] = useState('bulk-add');
  const [institutionId, setInstitutionId] = useState('demo-institution-1');
  const [groupName, setGroupName] = useState('');
  const [students, setStudents] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [groupProgress, setGroupProgress] = useState(null);
  const [comparativeData, setComparativeData] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleBulkAddCSV = async () => {
    if (!file || !institutionId) {
      alert('Lütfen dosya ve kurum ID girin');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('institution_id', institutionId);
      if (groupName) formData.append('group_name', groupName);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/institution/bulk-add-students-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        const error = await response.json();
        alert('Hata: ' + error.detail);
      }
    } catch (err) {
      alert('Bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupProgress = async () => {
    if (!institutionId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = groupName 
        ? `${API_URL}/api/institution/group-progress?institution_id=${institutionId}&group_name=${groupName}`
        : `${API_URL}/api/institution/group-progress?institution_id=${institutionId}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setGroupProgress(data);
      }
    } catch (err) {
      console.error('Error fetching group progress:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparativeAnalysis = async () => {
    if (!institutionId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/institution/comparative-analysis?institution_id=${institutionId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setComparativeData(data);
      }
    } catch (err) {
      console.error('Error fetching comparative data:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'name,email,grade\nAhmet Yılmaz,ahmet@example.com,11\nAyşe Demir,ayse@example.com,12';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ogrenci_sablonu.csv';
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center space-x-3">
            <Users className="h-8 w-8 text-indigo-600" />
            <span>Kurumsal Yönetim Paneli</span>
          </h1>
          <p className="text-gray-600">Toplu öğrenci yönetimi ve ilerleme takibi</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('bulk-add')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'bulk-add'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Upload className="inline h-5 w-5 mr-2" />
              Toplu Öğrenci Ekle
            </button>
            <button
              onClick={() => setActiveTab('progress')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'progress'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TrendingUp className="inline h-5 w-5 mr-2" />
              Grup İlerlemesi
            </button>
            <button
              onClick={() => setActiveTab('comparative')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'comparative'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="inline h-5 w-5 mr-2" />
              Karşılaştırmalı Analiz
            </button>
          </div>
        </div>

        {/* Bulk Add Tab */}
        {activeTab === 'bulk-add' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Toplu Öğrenci Ekle (CSV)</h2>
            
            <div className="mb-6">
              <button
                onClick={downloadTemplate}
                className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <Download className="h-5 w-5" />
                <span>Örnek CSV Şablonunu İndir</span>
              </button>
              <p className="text-sm text-gray-500 mt-2">
                CSV formatı: name,email,grade (başlık satırı zorunlu)
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kurum ID
                </label>
                <input
                  type="text"
                  value={institutionId}
                  onChange={(e) => setInstitutionId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="demo-institution-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grup Adı (Opsiyonel)
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="11-A, 12-B vb."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CSV Dosyası
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <button
                onClick={handleBulkAddCSV}
                disabled={loading || !file}
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Yükleniyor...' : 'Öğrencileri Ekle'}
              </button>
            </div>

            {result && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-bold text-green-900 mb-2">Sonuç:</h3>
                <p className="text-green-800">✅ {result.added_count} öğrenci başarıyla eklendi</p>
                {result.error_count > 0 && (
                  <div className="mt-2">
                    <p className="text-orange-800">⚠️ {result.error_count} hata</p>
                    <ul className="text-sm text-gray-700 mt-2 ml-4">
                      {result.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>• {err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Group Progress Tab */}
        {activeTab === 'progress' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Grup İlerleme Raporu</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kurum ID</label>
                <input
                  type="text"
                  value={institutionId}
                  onChange={(e) => setInstitutionId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grup Adı (Opsiyonel)</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <button
                onClick={fetchGroupProgress}
                disabled={loading}
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700"
              >
                {loading ? 'Yükleniyor...' : 'İlerlemeyi Getir'}
              </button>
            </div>

            {groupProgress && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Öğrenci Sayısı</p>
                    <p className="text-2xl font-bold text-blue-700">{groupProgress.student_count}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Toplam Soru</p>
                    <p className="text-2xl font-bold text-green-700">{groupProgress.summary.total_questions_attempted}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Ortalama Başarı</p>
                    <p className="text-2xl font-bold text-purple-700">{groupProgress.summary.average_accuracy}%</p>
                  </div>
                </div>

                {groupProgress.topic_breakdown.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Konu Bazlı Performans</h3>
                    <div className="space-y-2">
                      {groupProgress.topic_breakdown.slice(0, 10).map((topic, idx) => (
                        <div key={idx} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{topic.topic}</p>
                            <p className="text-sm text-gray-600">{topic.student_count} öğrenci • {topic.total_questions} soru</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg" style={{ color: topic.accuracy > 75 ? '#10b981' : topic.accuracy > 50 ? '#f59e0b' : '#ef4444' }}>
                              {topic.accuracy}%
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Comparative Analysis Tab */}
        {activeTab === 'comparative' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Karşılaştırmalı Analiz</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kurum ID</label>
                <input
                  type="text"
                  value={institutionId}
                  onChange={(e) => setInstitutionId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <button
                onClick={fetchComparativeAnalysis}
                disabled={loading}
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700"
              >
                {loading ? 'Yükleniyor...' : 'Analiz Yap'}
              </button>
            </div>

            {comparativeData && comparativeData.comparative_data.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Award className="h-6 w-6 text-yellow-500" />
                  <h3 className="text-lg font-bold text-gray-900">Grup Sıralaması</h3>
                </div>
                <div className="space-y-3">
                  {comparativeData.comparative_data.map((group, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border-2 ${
                        idx === 0 ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="text-2xl font-bold text-gray-400">#{idx + 1}</div>
                          <div>
                            <p className="font-bold text-gray-900">{group.group_name}</p>
                            <p className="text-sm text-gray-600">
                              {group.student_count} öğrenci • Öğrenci başına {group.questions_per_student} soru
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold" style={{ color: group.average_accuracy > 75 ? '#10b981' : '#f59e0b' }}>
                            {group.average_accuracy}%
                          </p>
                          <p className="text-sm text-gray-600">{group.total_questions} toplam soru</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstitutionManagementPage;